import { useState, type FC, useEffect, type Dispatch, type SetStateAction, useRef } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { type Stripe, loadStripe } from '@stripe/stripe-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type PreparePaymentRequest, type PaymentIntent, preparePayment } from '@/api/registration/payment';
import { type ApiResponse } from '@/api/tenantClient';
import PayByStripe from './PayByStripe';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useGroupCartQuery } from '@/registration/hook/useGroupCartQuery';
import { SkeletonText } from '@chakra-ui/react';
import { cartQueryKey } from '@/api/registration';

export interface PayByStripeWrapperProps {
  setErrors: Dispatch<SetStateAction<string[]>>
  isValid: boolean
}

const PayByStripeWrapper: FC<PayByStripeWrapperProps> = ({
  setErrors,
  isValid,
  ...props
}) => {
  const [options, setOptions] = useState<PaymentIntent>({
    id: '',
    clientSecret: '',
    publishableKey: '',
    gateway: 'stripe',
    order: {
      id: 0,
      secureHash: '',
      status: '',
      serial: '',
      event: '',
      tickets: [],
      attendee: { name: '', email: '' },
      subtotal: 0,
      tax: 0,
      totalPrice: 0,
      purchasedAt: '',
      paymentOption: '',
      eventId: '',
      orderItems: [],
      currencyCode: '',
      surcharge: 0,
      invoiceUrl: ''
    }
  });
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const { createTenantApiRequest } = useTenantApi();
  const { eventId } = useRegisterRoute();
  const queryClient = useQueryClient();
  const { data: cart } = useGroupCartQuery(eventId);

  const { mutate: preparePaymentMutate, isLoading } = useMutation<ApiResponse<PaymentIntent>, Error, PreparePaymentRequest>({
    mutationFn: async (preparePaymentData: PreparePaymentRequest) => {
      return await preparePayment(createTenantApiRequest)(preparePaymentData) as ApiResponse<PaymentIntent>;
    },
    onSuccess: (result) => {
      if (result.errors && result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        setOptions(result.items[0]);
        void queryClient.invalidateQueries({ queryKey: [cartQueryKey, { registrationId: cart?.registrationId }] });
        setStripePromise(loadStripe(result.items[0].publishableKey));
      }
    },
    onError: (error: any) => {
      setErrors([error.message ?? 'Failed to save the host.']);
    }
  });

  useEffect(() => {
    if (cart) {
      const preparePaymentData: PreparePaymentRequest = {
        cartId: cart.id,
        locale: 'aud',
        gateway: 'stripe'
      };
      setErrors([]);
      preparePaymentMutate(preparePaymentData);
    }
    // }
  }, [cart]);

  return isLoading ? (
    <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='8' />
  ) : options?.clientSecret && stripePromise && cart && (
    <Elements stripe={stripePromise} options={options}>
      <PayByStripe
        setErrors={setErrors}
        isValid={isValid}
        options={options}
        cart={cart}
      />
    </Elements>
  );
};

export default PayByStripeWrapper;
